/**
 * @fileoverview WeekIcon: draws the icon the server names for a plant's week caption (B136, week.icon). The server decides which
 * icon the week gets (a snowflake for cold nights, a sun for a warm quiet week, rain for a wet one); this draws the key it is
 * sent through the app's standard icons and works nothing out. A sky key ("snowflake", "sun", "cloud-rain") goes through
 * SkyIcon; an advice code of the week's marks ("CARE_COOL", "PROTECT_FROST") goes through adviceIcon. A key it does not know
 * draws nothing.
 *
 * Where: the plant card's weather caption (WeekCaption).
 * API: none.
 * Words: none.
 *
 * @module WeekIcon
 */

import React from 'react';
import type { LucideProps } from 'lucide-react';
import { SkyIcon } from '@/shared/components/SkyIcon';
import type { SkyKey } from '@/shared/api/header';
import { adviceIcon } from '@/features/plant-list/components/card/Verdict';

/** What the icon is given: the server's key, and what any Lucide icon takes (its size, stroke, class). */
export type WeekIconProps = Omit<LucideProps, 'name'> & {
  /** The server's icon key for the week (`week.icon`). */
  name: string | null | undefined;
};

/**
 * The server's week icon as a Lucide icon.
 *
 * @param props - WeekIconProps.
 * @returns The icon, or nothing for no key (or one it does not know).
 */
export const WeekIcon: React.FC<WeekIconProps> = ({ name, ...rest }) => {
  if (!name) return null;
  if (/^(CARE|PROTECT)_/.test(name)) {
    const Icon = adviceIcon(null, name);
    return <Icon {...rest} />;
  }
  return <SkyIcon sky={name as SkyKey} {...rest} />;
};
